import axios from "axios";
import { invalidateAllStoreCaches } from "./queryClient";

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BASE_URL}/api`;

const api = axios.create({ baseURL: API });

// Write methods that should trigger cache invalidation
const WRITE_METHODS = ["post", "put", "patch", "delete"];

// Endpoints that are admin write operations (not customer-facing or read-only)
const ADMIN_WRITE_PATTERNS = [
    "/products", "/categories", "/brands", "/settings",
    "/shipping-methods", "/payment-methods", "/banners",
    "/promotions", "/pricing-rules", "/import", "/stock",
];

api.interceptors.request.use(config => {
    // Customer pages use customer token, admin pages use admin token
    const customerToken = localStorage.getItem("sg_customer_token");
    const adminToken = localStorage.getItem("sg_admin_token");
    
    // If the request is for a customer endpoint, prefer customer token
    if (config.url?.startsWith("/customer/") && customerToken) {
        config.headers.Authorization = `Bearer ${customerToken}`;
    } else if (adminToken) {
        config.headers.Authorization = `Bearer ${adminToken}`;
    } else if (customerToken) {
        config.headers.Authorization = `Bearer ${customerToken}`;
    }
    return config;
});

api.interceptors.response.use(
    res => {
        // Auto-invalidate frontend caches after successful admin write operations
        const method = (res.config?.method || "").toLowerCase();
        const url = res.config?.url || "";
        if (WRITE_METHODS.includes(method) && ADMIN_WRITE_PATTERNS.some(p => url.includes(p))) {
            invalidateAllStoreCaches();
        }
        return res;
    },
    err => {
        if (err.response?.status === 401) {
            // Only clear tokens if this is a relevant auth failure
            const url = err.config?.url || "";
            if (url.startsWith("/customer/")) {
                localStorage.removeItem("sg_customer_token");
            } else {
                localStorage.removeItem("sg_admin_token");
                localStorage.removeItem("sg_admin_user");
            }
        }
        return Promise.reject(err);
    }
);

// Products
export const getProducts = (params) => api.get("/products", { params });
export const getProductParameterValues = (params) => api.get("/products/parameter-values", { params });
export const reindexProducts = () => api.post("/products/reindex");
export const getProduct = (id) => api.get(`/products/${id}`);
export const createProduct = (data) => api.post("/products", data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);
export const bulkAction = (data) => api.post("/products/bulk-action", data);
export const getBestsellers = (params) => api.get("/products/bestsellers", { params });
export const aiGenerateDescription = (data) => api.post("/products/ai-generate-description", data);
export const aiGenerateImage = (data) => api.post("/products/ai-generate-image", data);
export const getAutocompleteBrands = (q) => api.get("/products/autocomplete/brands", { params: { q } });
export const getAutocompleteManufacturers = (q) => api.get("/products/autocomplete/manufacturers", { params: { q } });
export const getNextSku = (prefix) => api.get("/products/next-sku", { params: { prefix } });
export const getPriceGroups = () => api.get("/price-groups");
export const createPriceGroup = (data) => api.post("/price-groups", data);
export const updatePriceGroup = (code, data) => api.put(`/price-groups/${code}`, data);
export const deletePriceGroup = (code) => api.delete(`/price-groups/${code}`);
export const exportPriceGroupPrices = (code) => `${API}/price-groups/${code}/export`;
export const getBrandConfig = (brandName) => api.get(`/brands-config/${encodeURIComponent(brandName)}`);
export const getImageStorageSettings = () => api.get("/settings/image-storage");
export const updateImageStorageSettings = (data) => api.put("/settings/image-storage", data);

// Categories
export const getCategories = (activeOnly = false, fresh = false) => api.get("/categories", { params: { active_only: activeOnly, ...(fresh && { fresh: true }) } });
export const getCategoryTree = (activeOnly = true) => api.get("/categories/tree", { params: { active_only: activeOnly } });
export const getCategoryPath = (id) => api.get(`/categories/${id}/path`);
export const createCategory = (data) => api.post("/categories", data);
export const updateCategory = (id, data) => api.put(`/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);
export const reorderCategories = (items) => api.put("/categories/reorder", { items });
export const bulkDeleteCategories = (ids) => api.post("/categories/bulk-delete", { ids });
export const bulkMoveCategories = (ids, target_parent_id) => api.post("/categories/bulk-move", { ids, target_parent_id });
export const moveCategoryPosition = (id, direction) => api.put(`/categories/${id}/move?direction=${direction}`);

// Orders
export const getOrders = (params) => api.get("/orders", { params });
export const createOrder = (data) => api.post("/orders", data);
export const getOrder = (id) => api.get(`/orders/${id}`);
export const updateOrderStatus = (id, data) => api.put(`/orders/${id}/status`, data);
export const deleteOrder = (id) => api.delete(`/orders/${id}`);
export const bulkDeleteOrders = (ids) => api.post("/orders/bulk-delete", { ids });
export const getOrderQrPayment = (orderId) => api.get(`/orders/${orderId}/qr-payment`);

// Customers
export const getCustomers = (params) => api.get("/customers", { params });
export const getCustomer = (id) => api.get(`/customers/${id}`);
export const createCustomer = (data) => api.post("/customers", data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);
export const deleteCustomer = (id) => api.delete(`/customers/${id}`);
export const bulkDeleteCustomers = (ids) => api.post("/customers/bulk-delete", { ids });
export const inlineUpdateCustomer = (id, data) => api.put(`/customers/${id}/inline`, data);
export const exportCustomersUrl = () => `${BASE_URL}/api/customers-export`;

// Search autocomplete
export const searchCustomers = (q) => api.get("/search/customers", { params: { q } });
export const searchProducts = (q) => api.get("/search/products", { params: { q } });
export const searchBrands = (q) => api.get("/search/brands", { params: { q } });

// Admin create order
export const adminCreateOrder = (data) => api.post("/orders/admin-create", data);
export const getOrderPdfUrl = (orderId) => `${BASE_URL}/api/orders/${orderId}/order-pdf`;
export const getOrderIsdocUrl = (orderId) => `${BASE_URL}/api/orders/${orderId}/isdoc`;
export const getCustomerOrderIsdocUrl = (orderId) => `${BASE_URL}/api/customer/orders/${orderId}/invoice-isdoc`;

// Leads
export const getLeads = (params) => api.get("/leads", { params });
export const createLead = (data) => api.post("/leads", data);
export const updateLead = (id, data) => api.put(`/leads/${id}`, data);
export const deleteLead = (id) => api.delete(`/leads/${id}`);

// Claims (Reklamace)
export const getClaims = (params) => api.get("/claims", { params });
export const getClaim = (id) => api.get(`/claims/${id}`);
export const createClaim = (data) => api.post("/claims", data);
export const updateClaim = (id, data) => api.put(`/claims/${id}`, data);
export const deleteClaim = (id) => api.delete(`/claims/${id}`);

// Returns (Vrácení)
export const getReturns = (params) => api.get("/returns", { params });
export const getReturn = (id) => api.get(`/returns/${id}`);
export const createReturn = (data) => api.post("/returns", data);
export const updateReturn = (id, data) => api.put(`/returns/${id}`, data);
export const deleteReturn = (id) => api.delete(`/returns/${id}`);

// Invoices (Faktury)
export const getInvoices = (params) => api.get("/invoices", { params });
export const getInvoice = (id) => api.get(`/invoices/${id}`);
export const createInvoice = (data) => api.post("/invoices", data);
export const updateInvoice = (id, data) => api.put(`/invoices/${id}`, data);
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`);
export const createInvoiceFromOrder = (orderId) => api.post(`/orders/${orderId}/create-invoice`);
export const getInvoicePdfUrl = (invoiceId) => `${BASE_URL}/api/invoices/${invoiceId}/pdf`;
export const getInvoiceIsdocUrl = (invoiceId) => `${BASE_URL}/api/invoices/${invoiceId}/isdoc`;
export const bulkIsdocExport = (ids) => api.post("/invoices/bulk-isdoc", { ids }, { responseType: "blob" });
export const bulkInvoiceAction = (ids, action) => api.post("/invoices/bulk-action", { ids, action });
export const bulkPdfExport = (ids) => api.post("/invoices/bulk-pdf", { ids }, { responseType: "blob" });

// Auth
export const login = (data) => api.post("/auth/login", data);
export const getMe = () => api.get("/auth/me");

// Stats
export const getDashboardStats = () => api.get("/stats/dashboard");

// Import
export const importCSV = (formData) => api.post("/import/csv", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const importXML = (formData) => api.post("/import/xml", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const getImportJobs = () => api.get("/import/jobs");
export const uploadImportPreview = (formData) => api.post("/import/upload-preview", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const getProductFields = () => api.get("/import/product-fields");
export const getImportRules = () => api.get("/import/rules");
export const getImportRule = (id) => api.get(`/import/rules/${id}`);
export const createImportRule = (data) => api.post("/import/rules", data);
export const updateImportRule = (id, data) => api.put(`/import/rules/${id}`, data);
export const deleteImportRule = (id) => api.delete(`/import/rules/${id}`);
export const duplicateImportRule = (id) => api.post(`/import/rules/${id}/duplicate`);
export const executeImportRule = (id) => api.post(`/import/rules/${id}/execute`);
export const getImportRuleStatus = (id) => api.get(`/import/rules/${id}/status`);
export const getImportJob = (id) => api.get(`/import/jobs/${id}`);
export const getImportTemplates = () => api.get("/import/templates");
export const saveImportTemplate = (data) => api.post("/import/templates", data);
export const deleteImportTemplate = (id) => api.delete(`/import/templates/${id}`);

// Export
export const getExportCSVUrl = () => `${BASE_URL}/api/export/products`;
export const getExportXlsxUrl = () => `${BASE_URL}/api/export/products/xlsx`;
export const getExportJsonUrl = () => `${BASE_URL}/api/export/products/json`;
export const getExportHeurekaUrl = () => `${BASE_URL}/api/export/heureka`;
export const startAiEnrich = (jobId, data) => api.post(`/import/jobs/${jobId}/ai-enrich`, data);
export const getAiEnrichStatus = (jobId) => api.get(`/import/jobs/${jobId}/ai-enrich-status`);
export const getOrderInvoiceUrl = (orderId) => `${BASE_URL}/api/orders/${orderId}/invoice-pdf`;
export const getStockExportExcelUrl = (params) => {
    const q = new URLSearchParams();
    if (params?.category_id) q.set("category_id", params.category_id);
    if (params?.stock_status) q.set("stock_status", params.stock_status);
    if (params?.search) q.set("search", params.search);
    return `${BASE_URL}/api/stock/export-excel?${q.toString()}`;
};
export const importStockExcel = (formData) => api.post("/stock/import-excel", formData, { headers: { "Content-Type": "multipart/form-data" } });

// Theme settings
export const getTheme = () => api.get("/settings/theme");
export const updateTheme = (data) => api.put("/settings/theme", data);
export const resetTheme = () => api.post("/settings/theme/reset");

// Blog
export const getBlogPosts = (params) => api.get("/blog/posts", { params });
export const getBlogPost = (id) => api.get(`/blog/posts/${id}`);
export const createBlogPost = (data) => api.post("/blog/posts", data);

// Brands
export const getBrands = () => api.get("/brands");

// Checkout
export const createCheckoutSession = (data) => api.post("/checkout/session", data);
export const getCheckoutStatus = (sessionId) => api.get(`/checkout/status/${sessionId}`);

// SMTP Settings
export const getSMTPSettings = () => api.get("/settings/smtp");
export const updateSMTPSettings = (data) => api.put("/settings/smtp", data);
export const testSMTP = (data) => api.post("/settings/smtp/test", data);

// Pricing Rules
export const getPricingRules = () => api.get("/pricing-rules");
export const createPricingRule = (data) => api.post("/pricing-rules", data);
export const updatePricingRule = (id, data) => api.put(`/pricing-rules/${id}`, data);
export const deletePricingRule = (id) => api.delete(`/pricing-rules/${id}`);
export const validateCoupon = (code) => api.post("/pricing-rules/validate-coupon", { code });

// Promotions (Bulk Sales)
export const getPromotions = () => api.get("/promotions");
export const createPromotion = (data) => api.post("/promotions", data);
export const updatePromotion = (id, data) => api.put(`/promotions/${id}`, data);
export const deletePromotion = (id) => api.delete(`/promotions/${id}`);
export const applyPromotion = (id) => api.post(`/promotions/${id}/apply`);
export const removePromotion = (id) => api.post(`/promotions/${id}/remove`);
export const previewPromotion = (id) => api.get(`/promotions/${id}/preview`);

// Countdown & Popup Promotions
export const getCountdownSettings = () => api.get("/promotions/countdown");
export const updateCountdownSettings = (data) => api.put("/promotions/countdown", data);
export const getPopupSettings = () => api.get("/promotions/popup");
export const updatePopupSettings = (data) => api.put("/promotions/popup", data);
export const getCrosssellRules = () => api.get("/promotions/crosssell");
export const createCrosssellRule = (data) => api.post("/promotions/crosssell", data);
export const updateCrosssellRule = (id, data) => api.put(`/promotions/crosssell/${id}`, data);
export const deleteCrosssellRule = (id) => api.delete(`/promotions/crosssell/${id}`);
export const getStockAlertSettings = () => api.get("/promotions/stock-alert-settings");
export const updateStockAlertSettings = (data) => api.put("/promotions/stock-alert-settings", data);
export const getFlashDealSettings = () => api.get("/promotions/flash-deal");
export const updateFlashDealSettings = (data) => api.put("/promotions/flash-deal", data);
export const getPromoPriority = () => api.get("/promotions/priority");
export const updatePromoPriority = (data) => api.put("/promotions/priority", data);

// Loyalty Program
export const getLoyaltySettings = () => api.get("/loyalty/settings");

// AI Price Check
export const runPriceCheck = (data) => api.post("/price-check/run", data);
export const getPriceCheckResults = (params) => api.get("/price-check/results", { params });
export const applyPriceRecommendation = (productId) => api.post(`/price-check/apply/${productId}`);
export const getPriceCheckSettings = () => api.get("/price-check/settings");
export const updatePriceCheckSettings = (data) => api.put("/price-check/settings", data);
export const getPriceCheckLog = (limit = 50) => api.get("/price-check/log", { params: { limit } });
export const updateLoyaltySettings = (data) => api.put("/loyalty/settings", data);
export const getLoyaltyBalance = () => api.get("/loyalty/balance");
export const getLoyaltyHistory = (page = 1) => api.get("/loyalty/history", { params: { page } });
export const redeemLoyaltyPoints = (data) => api.post("/loyalty/redeem", data);
export const getLoyaltyMembers = (params) => api.get("/loyalty/admin/members", { params });
export const adjustLoyaltyPoints = (data) => api.post("/loyalty/admin/adjust", data);
export const getLoyaltyStats = () => api.get("/loyalty/admin/stats");

// B2B Quotations
export const getQuotations = (params) => api.get("/quotations", { params });
export const createQuotation = (data) => api.post("/quotations", data);
export const getQuotation = (id) => api.get(`/quotations/${id}`);
export const updateQuotation = (id, data) => api.put(`/quotations/${id}`, data);
export const deleteQuotation = (id) => api.delete(`/quotations/${id}`);
export const addProductsToQuotation = (id, data) => api.post(`/quotations/${id}/add-products`, data);

// FVE Configurator
export const calculateFVE = (data) => api.post("/fve-configurator/calculate", data);
export const getConfiguratorSteps = () => api.get("/configurator/steps");
export const getConfiguratorProducts = (stepKey, params) => api.get(`/configurator/products/${stepKey}`, { params });
export const generateFVEOfferPdf = (data) => api.post("/fve-configurator/offer-pdf", data, { responseType: "blob" });

// PPL Tracking
export const getPPLTracking = (trackingNumber) => api.get(`/ppl/tracking/${trackingNumber}`);
export const getOrderTracking = (orderId) => api.get(`/orders/${orderId}/tracking`);

// CMS Pages
export const getCmsPages = (publishedOnly = false) => api.get("/cms/pages", { params: { published_only: publishedOnly } });
export const getCmsPage = (slug) => api.get(`/cms/pages/${slug}`);
export const createCmsPage = (data) => api.post("/cms/pages", data);
export const updateCmsPage = (slug, data) => api.put(`/cms/pages/${slug}`, data);
export const deleteCmsPage = (slug) => api.delete(`/cms/pages/${slug}`);

// Watermark & Image
export const getWatermarkSettings = () => api.get("/settings/watermark");
export const updateWatermarkSettings = (data) => api.put("/settings/watermark", data);
export const optimizeImage = (url, quality = 85, maxWidth = 1200, outputFormat = "webp") => api.post("/images/optimize", null, { params: { url, quality, max_width: maxWidth, output_format: outputFormat } });

// Image upload
export const uploadImage = (formData) => api.post("/images/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });

// Stock / Warehouse
export const getStock = (params) => api.get("/stock", { params });
export const updateStockSingle = (data, reason = "") => api.post(`/stock/update?reason=${encodeURIComponent(reason)}`, data);
export const updateStockBulk = (data) => api.post("/stock/bulk-update", data);
export const importStockCsv = (formData, operation = "set", reason = "CSV import") =>
    api.post(`/stock/import-csv?operation=${operation}&reason=${encodeURIComponent(reason)}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
export const getStockMovements = (params) => api.get("/stock/movements", { params });
export const getStockSummary = () => api.get("/stock/summary");

// Utils
export const slugifyText = (text) => api.get("/utils/slugify", { params: { text } });
export const getCategoryBrands = (slug) => api.get(`/categories/${slug}/brands`);

// Banners
export const getBanners = (position, categorySlug) => api.get("/banners", { params: { position, category_slug: categorySlug } });
export const getAllBanners = () => api.get("/banners/all");
export const createBanner = (data) => api.post("/banners", data);
export const updateBanner = (id, data) => api.put(`/banners/${id}`, data);
export const deleteBanner = (id) => api.delete(`/banners/${id}`);

// Reviews
export const getProductReviews = (productId, page = 1) => api.get(`/products/${productId}/reviews`, { params: { page } });
export const createReview = (productId, data) => api.post(`/products/${productId}/reviews`, data);
export const deleteReview = (id) => api.delete(`/reviews/${id}`);

// ARES
export const aresLookup = (ico) => api.get(`/ares/lookup/${ico}`);

// Address suggestions
export const suggestAddress = (q) => api.get("/address/suggest", { params: { q } });

// Brand logos
export const getBrandsConfig = () => api.get("/brands-config");
export const createBrandConfig = (data) => api.post("/brands-config", data);
export const updateBrandConfig = (name, data) => api.put(`/brands-config/${encodeURIComponent(name)}`, data);
export const deleteBrandConfig = (name) => api.delete(`/brands-config/${encodeURIComponent(name)}`);

// Theme settings aliases
export const getThemeSettings = getTheme;
export const updateThemeSettings = updateTheme;

// Image optimization
export const optimizeBatch = (data) => api.post("/images/optimize-batch", data);

// Team
export const getTeam = () => api.get("/team");
export const getTeamPublic = () => api.get("/team/public");
export const createTeamMember = (data) => api.post("/team", data);
export const updateTeamMember = (id, data) => api.put(`/team/${id}`, data);
export const deleteTeamMember = (id) => api.delete(`/team/${id}`);
export const getTeamRoles = () => api.get("/team/roles");
export const teamLogin = (data) => api.post("/team/login", data);

// Chatbot
export const getChatbotKnowledge = () => api.get("/chatbot/knowledge");
export const createChatbotKnowledge = (data) => api.post("/chatbot/knowledge", data);
export const updateChatbotKnowledge = (id, data) => api.put(`/chatbot/knowledge/${id}`, data);
export const deleteChatbotKnowledge = (id) => api.delete(`/chatbot/knowledge/${id}`);
export const getChatbotSettings = () => api.get("/chatbot/settings");
export const updateChatbotSettings = (data) => api.put("/chatbot/settings", data);
export const getChatbotHistory = () => api.get("/chatbot/history");
export const getChatbotSessionMessages = (sid) => api.get(`/chatbot/history/${sid}`);
export const sendChatMessage = (data) => api.post("/chatbot/chat", data);

// XML Feeds
export const getXmlFeedSettings = () => api.get("/xml-feeds/settings");
export const updateXmlFeedSettings = (data) => api.put("/xml-feeds/settings", data);
export const getXmlFeedStatus = () => api.get("/xml-feeds/status");
export const getXmlFeedCategoryMapping = () => api.get("/xml-feeds/category-mapping");
export const updateXmlFeedCategoryMapping = (items) => api.put("/xml-feeds/category-mapping", items);
export const validateXmlFeed = (type) => api.post(`/xml-feeds/validate/${type}`);
export const generateXmlFeed = (type) => api.post(`/xml-feeds/generate/${type}`);
export const getXmlFeedLogs = (params) => api.get("/xml-feeds/logs", { params });
export const getXmlFeedUrl = (type) => `${BASE_URL}/api/xml-feeds/${type}.xml`;

// Accessories batch
export const startAccessoriesBatch = (maxAccessories = 3) => api.post(`/accessories/batch/auto-assign?max_accessories=${maxAccessories}`);
export const getAccessoriesBatchStatus = () => api.get("/accessories/batch/status");
export const stopAccessoriesBatch = () => api.post("/accessories/batch/stop");

// Email Templates
export const getEmailSettings = () => api.get("/email/settings");
export const updateEmailSettings = (data) => api.put("/email/settings", data);
export const testEmail = (data) => api.post("/email/test", data);
export const getEmailTemplates = () => api.get("/email/templates");
export const getEmailTemplate = (id) => api.get(`/email/templates/${id}`);
export const createEmailTemplate = (data) => api.post("/email/templates", data);
export const updateEmailTemplate = (id, data) => api.put(`/email/templates/${id}`, data);
export const deleteEmailTemplate = (id) => api.delete(`/email/templates/${id}`);
export const getEmailVariables = () => api.get("/email/variables");
export const getEmailLogs = () => api.get("/email/logs");
export const getEmailPresets = () => api.get("/email/presets");
export const seedEmailTemplates = () => api.post("/email/seed-defaults");

// SMS Templates & GoSMS
export const getSmsSettings = () => api.get("/sms/settings");
export const updateSmsSettings = (data) => api.put("/sms/settings", data);
export const getSmsOrganization = () => api.get("/sms/organization");
export const testSms = (data) => api.post("/sms/test", data);
export const sendSms = (data) => api.post("/sms/send", data);
export const sendSmsFromOrder = (orderId, data) => api.post(`/sms/send-order/${orderId}`, data);
export const getSmsTemplates = () => api.get("/sms/templates");
export const getSmsTemplate = (id) => api.get(`/sms/templates/${id}`);
export const createSmsTemplate = (data) => api.post("/sms/templates", data);
export const updateSmsTemplate = (id, data) => api.put(`/sms/templates/${id}`, data);
export const deleteSmsTemplate = (id) => api.delete(`/sms/templates/${id}`);
export const getSmsVariables = () => api.get("/sms/variables");
export const getSmsLogs = (limit) => api.get("/sms/logs", { params: { limit } });
export const clearSmsLogs = () => api.delete("/sms/logs");
export const seedSmsTemplates = () => api.post("/sms/seed-defaults");


// Currencies
export const getCurrencies = () => api.get("/currencies");
export const createCurrency = (data) => api.post("/currencies", data);
export const updateCurrency = (id, data) => api.put(`/currencies/${id}`, data);
export const deleteCurrency = (id) => api.delete(`/currencies/${id}`);
export const seedCurrencies = () => api.post("/currencies/seed-defaults");

// Shipping Methods
export const getShippingMethods = () => api.get("/shipping-methods");
export const createShippingMethod = (data) => api.post("/shipping-methods", data);
export const updateShippingMethod = (id, data) => api.put(`/shipping-methods/${id}`, data);
export const deleteShippingMethod = (id) => api.delete(`/shipping-methods/${id}`);
export const seedShippingMethods = () => api.post("/shipping-methods/seed-defaults");

// Payment Methods
export const getPaymentMethods = () => api.get("/payment-methods");
export const createPaymentMethod = (data) => api.post("/payment-methods", data);
export const updatePaymentMethod = (id, data) => api.put(`/payment-methods/${id}`, data);
export const deletePaymentMethod = (id) => api.delete(`/payment-methods/${id}`);
export const seedPaymentMethods = () => api.post("/payment-methods/seed-defaults");

// Order Settings
export const getOrderSettings = () => api.get("/order-settings");
export const updateOrderSettings = (data) => api.put("/order-settings", data);
export const getNextOrderNumber = () => api.get("/order-settings/next-number");

// Monitoring & Diagnostics
export const getMonitoringEvents = (params) => api.get("/monitoring/events", { params });
export const getMonitoringStats = () => api.get("/monitoring/stats");
export const getMonitoringEventTypes = () => api.get("/monitoring/event-types");
export const getDiagnostics = () => api.get("/diagnostics");
export const optimizeDatabase = () => api.post("/diagnostics/optimize");

// Logo Upload
export const uploadLogo = (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/settings/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
};

// Admin Security – 2FA
export const get2FAStatus = () => api.get("/admin/2fa/status");
export const setup2FA = () => api.post("/admin/2fa/setup");
export const verify2FA = (code) => api.post("/admin/2fa/verify", { code });
export const disable2FA = (code) => api.post("/admin/2fa/disable", { code });

// Admin Security – Sessions & Logs
export const getAdminSessions = () => api.get("/admin/sessions");
export const revokeAdminSession = (sessionId) => api.delete(`/admin/sessions/${sessionId}`);
export const getLoginLog = () => api.get("/admin/login-log");
export const getSecuritySettings = () => api.get("/admin/security/settings");
export const updateSecuritySettings = (data) => api.put("/admin/security/settings", data);

// Admin – API Keys
export const getApiKeys = () => api.get("/admin/api-keys");
export const upsertApiKey = (data) => api.post("/admin/api-keys", data);
export const deleteApiKey = (keyName) => api.delete(`/admin/api-keys/${encodeURIComponent(keyName)}`);

// Admin – Sitemap
export const getSitemapStatus = () => api.get("/seo/sitemap/status");
export const regenerateSitemap = () => api.post("/seo/sitemap/regenerate");

// Admin – Sessions Dashboard
export const getAllSessions = () => api.get("/admin/sessions/all");
export const getSessionStats = () => api.get("/admin/sessions/stats");
export const forceRevokeSession = (sessionId) => api.delete(`/admin/sessions/${sessionId}/force`);

// Admin – A/B Testing
export const getABTests = () => api.get("/ab-tests");
export const createABTest = (data) => api.post("/ab-tests", data);
export const deleteABTest = (type, slug) => api.delete(`/ab-tests/${type}/${slug}`);
export const getABStats = (type, slug) => api.get(`/ab-tests/${type}/${slug}/stats`);
export const getABFullStats = (type, slug) => api.get(`/ab-tests/${type}/${slug}/full-stats`);
export const resolveABVariant = (type, slug) => api.get(`/ab-resolve/${type}/${slug}`);
export const trackABConversion = (data) => api.post("/ab-conversion", data);

// Admin – Logout
export const adminLogout = () => api.post("/auth/logout");

// Admin – Column Preferences (DB-persisted)
export const getAdminColumns = (viewKey) => api.get(`/settings/admin-columns/${viewKey}`);
export const saveAdminColumns = (viewKey, data) => api.put(`/settings/admin-columns/${viewKey}`, data);

// Admin – Warranties
export const getWarranties = (params) => api.get("/warranties", { params });
export const getWarranty = (id) => api.get(`/warranties/${id}`);
export const createWarranty = (data) => api.post("/warranties", data);
export const updateWarranty = (id, data) => api.put(`/warranties/${id}`, data);
export const deleteWarranty = (id) => api.delete(`/warranties/${id}`);
export const createWarrantiesFromInvoice = (invoiceId) => api.post(`/warranties/from-invoice/${invoiceId}`);
export const checkWarranty = (productId, email) => api.get(`/warranties/check/${productId}/${email}`);

export default api;
